import React, { useEffect, useState } from "react";
import { Box, LinearProgress, Link, Typography } from "@mui/material";
import { format } from "date-fns";
import { ParticipantToProgrammeType } from "../../models/modelTypes";
import { PrimaryButton, SecondaryButton } from "../layout/Button/Button";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { checkIfPrepared } from "../../client/api/session";
import { VCard } from "../../core/components/VCard";
import {
    aggregateSessions,
    countCompletedSessions,
    getNextSession,
    getParticipantProgrammes,
} from "../../utils/participantOverview";

type ParticipantSummarySidebarProps = {
    programmes: ParticipantToProgrammeType[];
    programmeIdFilter?: number;
};

export const ParticipantSummarySidebar: React.FC<ParticipantSummarySidebarProps> = ({
    programmes,
    programmeIdFilter,
}) => {
    const { push } = useAppNavigate();
    const filteredProgrammes = getParticipantProgrammes(programmes, programmeIdFilter);
    const allSessions = aggregateSessions(filteredProgrammes);
    const completedCount = countCompletedSessions(allSessions);
    const totalSessions = allSessions.length;
    const nextSession = getNextSession(allSessions);
    const progressPercent = totalSessions > 0 ? Math.round((completedCount / totalSessions) * 100) : 0;
    const [hasPrepared, setHasPrepared] = useState<boolean | null>(null);

    useEffect(() => {
        if (!nextSession?.id) {
            setHasPrepared(null);
            return;
        }

        checkIfPrepared(nextSession.id)
            .then(setHasPrepared)
            .catch(() => setHasPrepared(false));
    }, [nextSession?.id]);

    const sessionActionPath = nextSession
        ? hasPrepared
            ? `/deliver-session/${nextSession.id}`
            : `/prepare-session/${nextSession.id}`
        : undefined;
    const sessionActionLabel = hasPrepared ? "Deliver session" : "Prepare session";

    return (
        <VCard variant="outlined" sx={{ position: { md: "sticky" }, top: 24 }}>
            <Typography variant="h6" gutterBottom>
                Programme progress
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                {completedCount} of {totalSessions} sessions complete
            </Typography>
            <Box mb={2}>
                <LinearProgress variant="determinate" value={progressPercent} />
                <Typography variant="caption" color="textSecondary">
                    {progressPercent}% complete
                </Typography>
            </Box>

            {nextSession ? (
                <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Next session
                    </Typography>
                    <Typography variant="body2">
                        {nextSession.MappedStory?.Story?.storyName ?? `Session ${nextSession.sessionNumber}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                        {format(new Date(nextSession.date), "EEE d MMM yyyy")}
                    </Typography>
                    <Box mt={2} display="flex" flexDirection="column" gap={1}>
                        {sessionActionPath && hasPrepared !== null && (
                            <PrimaryButton size="small" onClick={() => push(sessionActionPath)}>
                                {sessionActionLabel}
                            </PrimaryButton>
                        )}
                        <SecondaryButton
                            size="small"
                            onClick={() => push(`/programmes/view/${nextSession.programmeId}?tab=sessions`)}
                        >
                            View programme
                        </SecondaryButton>
                    </Box>
                </Box>
            ) : (
                <Typography variant="body2" color="textSecondary">
                    No upcoming sessions
                </Typography>
            )}

            <ProgrammeLinks programmes={filteredProgrammes} />
        </VCard>
    );
};

const ProgrammeLinks: React.FC<{ programmes: ParticipantToProgrammeType[] }> = ({ programmes }) => {
    const { push } = useAppNavigate();

    if (programmes.length === 0) {
        return null;
    }

    return (
        <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
                Programmes
            </Typography>
            {programmes.map((ptp) => (
                <Link
                    key={ptp.id}
                    component="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => push(`/programmes/view/${ptp.programmeId}?tab=sessions`)}
                    sx={{ display: "block", textAlign: "left", mb: 0.5 }}
                >
                    {ptp.Programme.name}
                </Link>
            ))}
        </Box>
    );
};
